import {computed, ref, type ComputedRef, type Ref} from 'vue';
import {useUserStore} from '@/ts/store/user-store';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLogger} from '@/ts/composables/pure/use-logger';

/**
 * Drives the Instructions For Use notice (IPD-3216).
 *
 * The IFU is authored in Directus and republished without a deploy, so a revision used to reach
 * users only if they happened to open the page. The backend compares the published version against
 * the one stored for the user and answers with `ifuUpdateAvailable`; this composable only reacts to
 * it, so the rule for what counts as a new version lives in one place.
 *
 * The notice acknowledges, it does not consent: there is no decline path, and dismissing it never
 * costs the user access. Absent or false both mean "nothing to say" — only an explicit `true`
 * raises it, so a backend that could not read Directus stays quiet rather than notifying everyone.
 */
export function useIfuUpdateNotice(): {
  shouldShow: ComputedRef<boolean>;
  isAcknowledging: Ref<boolean>;
  acknowledge(): Promise<void>;
} {
  const userStore = useUserStore();
  const api = useApi();
  const logger = useLogger('useIfuUpdateNotice');
  const isAcknowledging = ref(false);
  let pending: Promise<void> | null = null;

  const shouldShow = computed(() => userStore.userData?.ifuUpdateAvailable === true);

  /**
   * On failure the flag is left as it was, so the notice returns on the next `/me` rather than
   * being silently lost — the user has not been recorded as having seen anything.
   */
  async function record(): Promise<void> {
    isAcknowledging.value = true;
    try {
      const updatedUser = await api.me.acknowledgeIfu();
      userStore.setUserData(updatedUser);
    } catch (error) {
      logger.error('Failed to record the IFU acknowledgement:', error)();
    }
    isAcknowledging.value = false;
  }

  /** A second click while the first is in flight joins it rather than recording the version twice. */
  const acknowledge = async(): Promise<void> => {
    pending ??= record().finally(() => {
      pending = null;
    });
    return pending;
  };

  return {shouldShow, isAcknowledging, acknowledge};
}
